<template>
  <v-card class="wp-scope-filters col-box">
    <div class="wp-scope-filters__header mb-1">
      <h3 class="wp-scope-filters__input-heading">
        {{ t('workpackagePage.splitWorkpackages.splitWorkpackagesTitle') }}

        <!-- Filters tooltip -->
        <AssortmentTooltip
          :title="t('tooltips.filterSplitWorkpackages.title')"
          :tooltip-sections="filterSplitWorkpackagesSections"
        />
      </h3>
    </div>

    <v-row class="ma-0">
      <v-col cols="12" class="pa-0">
        <WpScopeFiltersChain
          :wp-furniture="wpFurniture"
          @update-main-filter-object="updateMainFilterObject"
        />
      </v-col>

      <v-col cols="12" class="pa-0">
        <WpScopeFiltersAssortmentSeries
          :wp-furniture="wpFurniture"
          @update-main-filter-object="updateMainFilterObject"
        />
      </v-col>

      <v-col cols="12" class="pa-0">
        <WpScopeFiltersAssortmentRole
          :wp-furniture="wpFurniture"
          @update-main-filter-object="updateMainFilterObject"
        />
      </v-col>

      <v-col cols="12" class="pa-0">
        <WpScopeFiltersDisplayGroup
          :products="products"
          @update-main-filter-object="updateMainFilterObject"
        />
      </v-col>

      <v-col cols="12" class="pa-0">
        <WpScopeFiltersCdtSelect
          :products="products"
          @update-main-filter-object="updateMainFilterObject"
        />
      </v-col>
    </v-row>

    <v-row class="footer-panel ma-0">
      <v-col class="d-flex justify-end filter-apply colspan">
        <v-tooltip
          :disabled="!filterButtonDisabledState.isDisabled"
          :text="filterButtonDisabledState.reason"
          location="bottom"
        >
          <template #activator="{ props }">
            <span v-bind="props">
              <v-btn
                data-id-e2e="btnFilterSplitWorkpackages"
                variant="flat"
                primary
                min-width="65"
                :disabled="filterButtonDisabledState.isDisabled"
                :loading="isSaving"
                @click="filterSplitWorkpackages()"
              >
                {{ t('workpackagePage.splitWorkpackages.splitWorkpackagesSave') }}
              </v-btn>
            </span>
          </template>
          <span class="tooltip-text">{{ filterButtonDisabledState.reason }}</span>
        </v-tooltip>
      </v-col>
    </v-row>
    <DependencyTreeFeedbackModal
      :value="dependencyTreeModalOpen"
      :results="dependencyTreeFeedback"
      page="workpackageSetup"
      @close="closeDependencyTreeModal"
      @commit="filterSplitWorkpackages(true)"
    />
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { cloneDeep, sortBy, indexOf, get, groupBy, isEmpty, size } from 'lodash';

// enums
import filterTypeEnum from '@enums/wp-filter-types';
import originSourceTypes from '@enums/origin-source-types';

// composables
import { useFilterSplitWorkpackages } from '@/composables/use-tooltip-options';
import { useInternationalization } from '@/composables/use-internationalization';

// stores
import { useWorkpackagesStore } from '@/stores/workpackages';
import { useFurnitureStore } from '@/stores/furniture';
import { useScenariosStore } from '@/stores/scenarios';
import { useSnackbarStore } from '@/stores/snackbar';
import { useDependencyTreeStore } from '@/stores/dependency-tree';
import { useWorkpackageProductsStore } from '@/stores/workpackage-products';

// Default filters don't include any data, but in practice
// that means everything is included. Once something has changed,
// the filters will be used, and only the filtered values will apply.
const defaultFilters = () => ({
  haveDisplayGroupsChanged: false,
  haveCdtsChanged: false,
  haveChainsChanged: false,
  haveRolesChanged: false,
  haveSeriesChanged: false,
  [filterTypeEnum.displayGroup]: [],
  [filterTypeEnum.cdt]: [],
  [filterTypeEnum.chain]: [],
  [filterTypeEnum.role]: [],
});

const CHAIN_CFC = 'CFC';

export default {
  setup() {
    const { filterSplitWorkpackagesSections } = useFilterSplitWorkpackages();
    const { t } = useInternationalization();
    return { filterSplitWorkpackagesSections, t };
  },

  data() {
    return {
      filters: defaultFilters(),
      dependencyTreeFeedback: {},
      dependencyTreeModalOpen: false,
      isSaving: false,
    };
  },

  computed: {
    ...mapState(useWorkpackagesStore, [
      'selectedWorkpackage',
      'loading',
      'missingProducts',
      'isImportingFromPlano',
      'lastSuccessfulImportFromPlanoTimestamp',
    ]),
    ...mapState(useFurnitureStore, ['getWorkpackageFurniture']),
    ...mapState(useWorkpackageProductsStore, ['workpackageProducts']),

    wpFurniture() {
      return this.getWorkpackageFurniture;
    },

    products() {
      const products = this.workpackageProducts;
      return sortBy(cloneDeep(products), 'productKey');
    },

    isWorkpackageChainCfc() {
      const wpChain = get(this.selectedWorkpackage, 'selectedChain', '');
      return wpChain === CHAIN_CFC;
    },

    isFilteredToCfcOnly() {
      const seriesFilter = this.filters[filterTypeEnum.series];

      return size(seriesFilter) === 1 && seriesFilter[0] === CHAIN_CFC;
    },

    filterButtonDisabledState() {
      if (this.isSaving) {
        return {
          isDisabled: true,
          reason: this.$t('workpackagePage.splitWorkpackages.disabledTooltips.applyingFilters'),
        };
      }

      if (this.loading) {
        return {
          isDisabled: true,
          reason: this.$t('workpackagePage.splitWorkpackages.disabledTooltips.loadingFilterValues'),
        };
      }

      if (this.isImportingFromPlano) {
        return {
          isDisabled: true,
          reason: this.$t(
            'workpackagePage.splitWorkpackages.disabledTooltips.importingFromAssortmentGroups'
          ),
        };
      }

      return { isDisabled: false };
    },
  },

  watch: {
    /**
     * Watch for successful imports from planogram because in some cases, we need
     * to update the filters following the import.
     */
    lastSuccessfulImportFromPlanoTimestamp: {
      async handler(newTimestamp) {
        // The value of the timestamp isn't used, we just use the fact it has changed to a
        // non-falsy value to identify a successful import.
        if (newTimestamp) {
          await this.handleImportFromPlanoSuccess();
        }
      },
    },
  },

  created() {
    this.init();
  },

  methods: {
    ...mapActions(useWorkpackageProductsStore, [
      'fetchWorkpackageProducts',
      'saveWorkpackageProductSelection',
    ]),
    ...mapActions(useScenariosStore, ['setSelectedScenario']),
    ...mapActions(useDependencyTreeStore, ['triggerDependencyTree']),
    ...mapActions(useWorkpackagesStore, ['refreshWorkpackageInformation', 'updateWorkpackage']),
    ...mapActions(useFurnitureStore, ['fetchWorkpackageFurniture', 'updateWorkpackageFurniture']),
    ...mapActions(useSnackbarStore, ['showError']),

    async init() {
      this.fetchWorkpackageFurniture();
    },

    /**
     *  Update the filter within the filter object
     *
     * @param {Object} RORO
     * @param {"cdt" | "displayGroup"} RORO.type Filter type to update - should come from filterType enum
     * @param {String[]} RORO.update Product keys to add to removal filter
     */
    updateMainFilterObject({ type, update, isInit = false }) {
      this.filters[type] = update;
      if (isInit) {
        return;
      }

      // todo switch
      if (type === filterTypeEnum.displayGroup) {
        this.filters.haveDisplayGroupsChanged = true;
      } else if (type === filterTypeEnum.cdt) {
        this.filters.haveCdtsChanged = true;
      } else if (type === filterTypeEnum.chain) {
        this.filters.haveChainsChanged = true;
      } else if (type === filterTypeEnum.role) {
        this.filters.haveRolesChanged = true;
      } else if (type === filterTypeEnum.series) {
        this.filters.haveSeriesChanged = true;
      }
    },

    getFilterChanges() {
      let wpFurnitureFilterResults;
      let wpProductsFilterResult;
      // 1. Get the workpackage products that should be removed and kept based upon display group and CDT filters.
      if (this.filters.haveDisplayGroupsChanged || this.filters.haveCdtsChanged) {
        wpProductsFilterResult = this.getWorkpackageProductsFilterResult();
      }

      // 2. Get the workpackage furniture that should be kept and removed based upon the chain, role and series filters.
      if (
        (this.filters.haveChainsChanged && !isEmpty(this.filters[filterTypeEnum.chain])) ||
        (this.filters.haveRolesChanged && !isEmpty(this.filters[filterTypeEnum.role])) ||
        (this.filters.haveSeriesChanged && !isEmpty(this.filters[filterTypeEnum.series]))
      ) {
        wpFurnitureFilterResults = this.getWorkpackageFurnitureFilterResults();
      }

      return { wpFurnitureFilterResults, wpProductsFilterResult };
    },

    async filterSplitWorkpackages(commit = false) {
      const filterChanges = this.getFilterChanges();
      const wpFurnitureFilterResults = filterChanges.wpFurnitureFilterResults;
      let wpProductsFilterResult = filterChanges.wpProductsFilterResult;

      const {
        wpFurnitureFilterResult,
        wpProductFurnitureFilterResult,
        wpStoreFurnitureFilterResult,
      } = wpFurnitureFilterResults || {};

      try {
        const results = await this.triggerDependencyTree({
          params: {
            change: 'workpackageSetupModified',
            updates: {
              global: {
                productCount: { total: wpProductsFilterResult?.keep?.length || 0 },
                // TODO: AOV3-8138 evaluate the dependant WP collections that are affected by these
                productTofurnitureCount: {
                  total: wpProductFurnitureFilterResult?.keep?.length || 0,
                },
                furnitureCount: { total: wpFurnitureFilterResult?.keep?.length || 0 },
                storeToFurnitureCount: { total: wpStoreFurnitureFilterResult?.keep?.length || 0 },
              },
            },
            commit,
            workpackageId: this.selectedWorkpackage._id,
          },
        });
        if (results.needsFeedback) {
          this.dependencyTreeFeedback = results.output;
          this.dependencyTreeModalOpen = true;
          return false;
        }

        this.isSaving = true;

        if (wpFurnitureFilterResults) {
          // 3. Delete any furniture that has now been filtered out.
          await this.updateWpFurniture({
            wpFurnitureFilterResult,
            wpProductFurnitureFilterResult,
            wpStoreFurnitureFilterResult,
          });

          if (!isEmpty(wpStoreFurnitureFilterResult.remove)) {
            // 4. Filter workpackage stores based upon the filtered furniture. If a store no longer has any furniture,
            // the store itself will be updated to "in scope for analysis only". The exception to this is when filtered
            // to CFC only, where stores with no furniture are removed from the workpackage entirely. This is because
            // CFC is online-only so using physical stores for analysis isn't appropriate.
            const storeKeysWithoutFurniture = this.getWorkpackageStoreKeysWithoutFurniture({
              wpStoreFurnitureFilterResult,
            });

            const { stores } = this.selectedWorkpackage;
            const storeUpdates = this.isFilteredToCfcOnly
              ? this.getCfcOnlyStoreUpdates({
                  stores,
                  storeKeysWithoutFurniture,
                })
              : this.getStoreUpdates({
                  stores,
                  storeKeysWithoutFurniture,
                });

            // 5. Update/delete workpackage stores that have now been filtered.
            await this.updateWorkpackage({
              workpackageId: this.selectedWorkpackage._id,
              updates: { stores: storeUpdates },
            });
          }

          // 6. Apply any CFC-specific filtering to workpackage products.
          // For CFC only, we need to remove products that no longer have any furniture (rather than just marking
          // them as for analysis only)
          if (this.isFilteredToCfcOnly && wpProductFurnitureFilterResult) {
            const { updatedWpProductsFilterResult } = this.getCfcOnlyWpProductsFilterResult({
              wpProductsFilterResult,
              wpProductFurnitureFilterResult,
            });
            wpProductsFilterResult = updatedWpProductsFilterResult;
          }
        }

        if (wpProductsFilterResult) {
          // 7. Delete any products that have now been filtered out.
          const productKeysToDelete = wpProductsFilterResult.remove?.map(p => p.productKey) || [];
          await this.updateWorkpackageProducts(productKeysToDelete);
        }

        // The following fetches have loading built in and can take over from here
        await this.refreshWorkpackageInformation({ workpackageId: this.selectedWorkpackage._id });
        // Reset the filters to be empty arrays
        this.filters = defaultFilters();
      } catch (e) {
        this.showError(this.$t('notifications.filterFurnitureProductsStores.failed'));
        console.error(e);
      } finally {
        this.isSaving = false;
      }
    },

    /**
     * Gets the updates that need to be applied to the provided workpackage stores based upon the store keys
     * that do not have any furniture. These stores are updated to be analysis only.
     * @param { stores: Array<Object>, storeKeysWithoutFurniture: Set<string> }
     * @returns { Array<Object> } The workpackage stores with the stores without furniture updated to be analysis only.
     */
    getStoreUpdates({ stores, storeKeysWithoutFurniture }) {
      const storeUpdates = stores.map(store => {
        if (storeKeysWithoutFurniture.has(store.storeKey)) {
          store.assortment = false;
        }
        return store;
      });

      return storeUpdates;
    },

    /**
     * Gets the updates that need to be applied to the provided workpackage stores based upon the store keys
     * that do not have any furniture. For CFC only, these stores are removed entirely.
     * @param { stores: Array<Object>, storeKeysWithoutFurniture: Set<string> }
     * @returns { Array<Object> } The workpackage stores with the stores without furniture removed.
     */
    getCfcOnlyStoreUpdates({ stores, storeKeysWithoutFurniture }) {
      return stores.filter(store => !storeKeysWithoutFurniture.has(store.storeKey));
    },

    /**
     * Gets the unique set of workpackage store keys that no longer have any furniture associated with them.
     * @param { wpStoreFurnitureFilterResult: FilterResult }
     * @returns { Set<string> } The unique set of store keys.
     */
    getWorkpackageStoreKeysWithoutFurniture({ wpStoreFurnitureFilterResult }) {
      const removedFurnitureStoreKeys = new Set(
        (wpStoreFurnitureFilterResult.remove || []).map(sf => sf.storeKey)
      );
      const keptFurnitureStoreKeys = new Set(
        (wpStoreFurnitureFilterResult.keep || []).map(sf => sf.storeKey)
      );

      // Get stores that had furniture removed and now have no remaining furniture.
      const storeKeysWithoutFurniture = new Set(
        [...removedFurnitureStoreKeys].filter(key => !keptFurnitureStoreKeys.has(key))
      );

      return storeKeysWithoutFurniture;
    },

    /**
     * Applies the display group and CDT filters to the workpackage products and returns the filtering result.
     * Note that this function does not mutate any data, it only returns the filtering result.
     * @returns { {'remove': Array | undefined, 'keep': Array | undefined} }
     * Result of filtering workpackage products based on the selected display group and CDT filters.
     * Items in the 'remove' array are those that would be filtered out and 'keep' are those that would remain.
     */
    getWorkpackageProductsFilterResult() {
      // Group products into 'remove' and 'keep' categories
      const productsFilterResult = groupBy(
        this.products,
        ({ isDisplayable, productCategoryKey, originSource }) => {
          // It is possible for semi-new products to have missing isDisplayable and/or productCategoryKey values.
          // Whenever this is the case, we should NOT filter the product
          const isNewProduct = originSource === originSourceTypes.newInTemplate;

          // Remove products from unselected CDTs or DisplayGroups
          const isDGSelectedForRemoval =
            isNewProduct && !isDisplayable
              ? false
              : this.filters.haveDisplayGroupsChanged &&
                indexOf(this.filters.displayGroup, isDisplayable) === -1;
          const isCDTSelectedForRemoval =
            isNewProduct && !productCategoryKey
              ? false
              : this.filters.haveCdtsChanged &&
                indexOf(this.filters.cdt, productCategoryKey) === -1;

          if (isDGSelectedForRemoval || isCDTSelectedForRemoval) {
            return 'remove';
          }

          return 'keep';
        }
      );

      return productsFilterResult;
    },

    /**
     * Applies the chain, role and series filters to the workpackage furniture and returns the filtering result.
     * Note that this function does not mutate any data, it only returns the filtering result.
     * @returns {{
     *    wpFurnitureFilterResult: FilterResult,
     *    wpProductFurnitureFilterResult: FilterResult,
     *    wpStoreFurnitureFilterResult: FilterResult
     * }}
     * Result of filtering workpackage furniture based on selected chain, role and series filters.
     * Items in the 'remove' array are those that would be filtered out and 'keep' are those that would remain.
     */
    getWorkpackageFurnitureFilterResults() {
      const result = {
        wpFurnitureFilterResult: {},
        wpProductFurnitureFilterResult: {},
        wpStoreFurnitureFilterResult: {},
      };

      // Group the workpackage furniture into 'remove' and 'keep' categories based upon the selected filters.
      const wpFurnitureResult = groupBy(this.wpFurniture.furniture, f => {
        const furnitureChain = get(f, 'clientProvidedMetadata.furnitureChain', null);
        const furnitureRole = get(f, 'clientProvidedMetadata.furnitureAssortmentRole', null);
        const furnitureSeries = get(f, 'clientProvidedMetadata.furnitureAssortmentSeries', null);

        // Check each filter condition - if filter has values and item doesn't match, remove it
        const chainFilter = this.filters[filterTypeEnum.chain];
        const roleFilter = this.filters[filterTypeEnum.role];
        const seriesFilter = this.filters[filterTypeEnum.series];

        const notInChain = size(chainFilter) && !chainFilter.includes(furnitureChain);
        const notInRole = size(roleFilter) && !roleFilter.includes(furnitureRole);
        const notInSeries = size(seriesFilter) && !seriesFilter.includes(furnitureSeries);

        if (notInChain || notInRole || notInSeries) {
          return 'remove';
        }

        return 'keep';
      });

      result.wpFurnitureFilterResult = wpFurnitureResult;

      // If no furniture has been removed, then no store or product furniture links will be removed either so return early.
      if (isEmpty(wpFurnitureResult.remove)) {
        return result;
      }

      const removedFurnitureIds = wpFurnitureResult.remove.map(f => f._id);

      // Furniture has links to which products were on that furniture in a given time period.
      // These links need to be removed for any furniture that has been removed.
      const wpProductFurnitureResult = groupBy(this.wpFurniture.productFurniture, pf =>
        removedFurnitureIds.includes(pf.furnitureId) ? 'remove' : 'keep'
      );

      // Furniture has links to which stores that furniture was present in, in a given time period.
      // These links need to be removed for any furniture that has been removed.
      const wpStoreFurnitureResult = groupBy(this.wpFurniture.storeFurniture, sf =>
        removedFurnitureIds.includes(sf.furnitureId) ? 'remove' : 'keep'
      );

      result.wpProductFurnitureFilterResult = wpProductFurnitureResult;
      result.wpStoreFurnitureFilterResult = wpStoreFurnitureResult;

      return result;
    },

    /**
     * Applies CFC-specific filtering to the workpackage products and returns the filter result.
     * Specifically, for CFC, any products without furniture should be removed from the workpackage.
     * Note that this function does not mutate any data, it only returns an amended version of wpProductsFilterResult.
     * @param { wpProductsFilterResult: FilterResult | undefined, wpProductFurnitureFilterResult: FilterResult }
     * @returns { updatedWpProductsFilterResult: FilterResult } Returns an updated version of the supplied wpProductsFilterResult
     * where any products without furniture have been moved from the 'keep' array to the 'remove' array.
     */
    getCfcOnlyWpProductsFilterResult({ wpProductsFilterResult, wpProductFurnitureFilterResult }) {
      // Determine which product keys are now without furniture.
      const removedFurnitureProductKeys = new Set(
        (wpProductFurnitureFilterResult.remove || []).flatMap(pf => pf.productKeys)
      );
      const keptFurnitureProductKeys = new Set(
        (wpProductFurnitureFilterResult.keep || []).flatMap(pf => pf.productKeys)
      );
      const productKeysWithoutFurniture = new Set(
        [...removedFurnitureProductKeys].filter(key => !keptFurnitureProductKeys.has(key))
      );

      // If we haven't already applied filtering to products (present in wpProductsFilterResult), then
      // we apply filtering to this.products directly
      if (!wpProductsFilterResult) {
        const filterResult = groupBy(this.products, product => {
          return productKeysWithoutFurniture.has(product.productKey) ? 'remove' : 'keep';
        });

        return { updatedWpProductsFilterResult: filterResult };
      }

      // When wpProductsFilterResult already has some product filtering, apply the CFC-specific filtering on top of that.
      const updatedWpProductsFilterResult = { ...wpProductsFilterResult };

      // When no product furniture is being removed, then no products will need to be removed.
      if (isEmpty(wpProductFurnitureFilterResult.remove)) {
        return { updatedWpProductsFilterResult };
      }

      // Any products without furniture inside updatedWpProductsFilterResult.keep should be moved to
      // updatedWpProductsFilterResult.remove since the products themselves should be removed.
      const { remove, keep } = groupBy(wpProductsFilterResult?.keep || [], product => {
        return productKeysWithoutFurniture.has(product.productKey) ? 'remove' : 'keep';
      });
      updatedWpProductsFilterResult.keep = keep;
      updatedWpProductsFilterResult.remove = [
        ...(updatedWpProductsFilterResult.remove || []),
        ...(remove || []),
      ];

      return { updatedWpProductsFilterResult };
    },

    /**
     * Updates the workpackage furniture to only include those marked as 'keep' in the filter results.
     * No changes are made if there are no filter results.
     * @param {
     *  wpFurnitureFilterResult: FilterResult,
     *  wpProductFurnitureFilterResult: FilterResult,
     *  wpStoreFurnitureFilterResult: FilterResult,
     * }
     */
    async updateWpFurniture({
      wpFurnitureFilterResult,
      wpProductFurnitureFilterResult,
      wpStoreFurnitureFilterResult,
    }) {
      // If nothing is being removed, then .keep will be identical to the existing furniture so no update is needed.
      if (isEmpty(wpFurnitureFilterResult.remove)) {
        return;
      }

      const wpFurnitureUpdates = {
        ...this.wpFurniture,
        furniture: wpFurnitureFilterResult.keep,
        productFurniture: wpProductFurnitureFilterResult.keep,
        storeFurniture: wpStoreFurnitureFilterResult.keep,
      };

      await this.updateWorkpackageFurniture({
        workpackageId: this.wpFurniture.workpackageId,
        updates: wpFurnitureUpdates,
      });
    },

    async updateWorkpackageProducts(productKeysToDelete = []) {
      await this.saveWorkpackageProductSelection({
        workpackageId: this.selectedWorkpackage._id,
        productUpdates: [],
        productKeysToAdd: [],
        productKeysToDelete,
        categoryKeysToAdd: [],
        categoryKeysToDelete: [],
      });

      await this.fetchWorkpackageProducts({ workpackageId: this.selectedWorkpackage._id });
    },

    closeDependencyTreeModal() {
      this.dependencyTreeModalOpen = false;
      this.dependencyTreeFeedback = {};
    },

    /**
     * For CFC workpackages, when import from AGs/planogram has succeeded, we automatically filter the
     * set of furniture, stores, and products down to only those that are for CFC.
     */
    async handleImportFromPlanoSuccess() {
      const seriesFilter = this.filters[filterTypeEnum.series];
      if (!this.isWorkpackageChainCfc || !seriesFilter?.includes(CHAIN_CFC)) {
        return;
      }
      this.updateMainFilterObject({
        type: filterTypeEnum.series,
        update: [CHAIN_CFC],
      });

      await this.filterSplitWorkpackages();
    },
  },
};

/**
 * @typedef {Object} FilterResult
 * @property {Array|undefined} remove Items that would be filtered out.
 * Can be undefined when no items would be filtered out.
 * @property {Array|undefined} keep Items that would remain after filtering.
 * Can be undefined when no items would remain.
 */
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.tooltip-text {
  font-size: 1.25rem;
}

.wp-scope-filters {
  font-size: 1.2rem;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__input-heading {
    font-size: 1.2rem;
    color: $assortment-workpackage-text;

    span {
      font-weight: normal;
    }
  }

  .filter-cols {
    padding-top: 0px !important;
    padding-bottom: 0px !important;
  }

  .filter-apply {
    padding-right: 0;
  }

  .filter-main {
    width: 100%;
    align-items: baseline;
  }

  .filter-box {
    align-items: center;

    span {
      padding-left: 0;
    }
  }
}
</style>
